import React, { useState, useEffect } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { authService } from '../services/authService';
import LyricConfirm from './LyricConfirm';

const UserProfile = ({ style = {} }) => {
  const { oktaAuth, authState } = useOktaAuth();
  const [userInfo, setUserInfo] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);

  useEffect(() => {
    if (authState?.isAuthenticated) {
      oktaAuth.getUser().then(info => {
        setUserInfo(info);
      }).catch(err => {
        console.error('Error getting user info:', err);
      });
    }
  }, [authState, oktaAuth]);

  const handleLogout = async () => {
    setConfirmLogout(false);
    try { sessionStorage.setItem('lyric.justLoggedOut', '1'); } catch { /* ignore */ }
    await authService.destroySession();
    try { await oktaAuth.tokenManager.clear(); } catch { /* ignore */ }
    oktaAuth.signOut();
  };

  if (!authState?.isAuthenticated || !userInfo) {
    return null;
  }

  return (
    <div style={{ ...styles.container, ...style }}>
      <div 
        style={styles.profileButton}
        onClick={() => setShowDropdown(!showDropdown)}
      >
        <div style={styles.avatar}>
          {userInfo.name ? userInfo.name.charAt(0).toUpperCase() : 'U'}
        </div>
        <span style={styles.userName}>{userInfo.name || userInfo.email}</span>
        <span style={styles.dropdownArrow}>▼</span>
      </div>
      
      {showDropdown && (
        <div style={styles.dropdown}>
          <div style={styles.dropdownHeader}>
            <div style={styles.userDetails}>
              <strong>{userInfo.name || 'User'}</strong>
              <div style={styles.userEmail}>{userInfo.email}</div>
            </div>
          </div>
          <hr style={styles.divider} />
          <button 
            style={styles.logoutButton} 
            onClick={() => { setShowDropdown(false); setConfirmLogout(true); }}
          >
            🚪 Sign Out
          </button>
        </div>
      )}

      <LyricConfirm
        isOpen={confirmLogout}
        title="Sign out of Lyric?"
        message="Are you sure you want to sign out? You'll need to sign in with Okta again to get back in."
        confirmLabel="Yes, sign out"
        cancelLabel="No, stay signed in"
        tone="danger"
        onConfirm={handleLogout}
        onCancel={() => setConfirmLogout(false)}
      />
    </div>
  );
};

const styles = {
  container: {
    position: 'relative',
    display: 'inline-block',
  },
  profileButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    backgroundColor: '#f8f9fa',
    border: '1px solid #dee2e6',
    borderRadius: '25px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  avatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: '#007bff',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: '0.9rem',
  },
  userName: {
    fontWeight: '500',
    color: '#333',
    maxWidth: '150px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  dropdownArrow: {
    fontSize: '0.7rem',
    color: '#666',
    transition: 'transform 0.2s ease',
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    right: '0',
    marginTop: '0.5rem',
    backgroundColor: '#ffffff',
    border: '1px solid #dee2e6',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    minWidth: '250px',
    zIndex: 1000,
  },
  dropdownHeader: {
    padding: '1rem',
  },
  userDetails: {
    textAlign: 'left',
  },
  userEmail: {
    color: '#666',
    fontSize: '0.9rem',
    marginTop: '0.25rem',
  },
  divider: {
    margin: '0',
    border: 'none',
    borderTop: '1px solid #e9ecef',
  },
  logoutButton: {
    width: '100%',
    padding: '0.75rem 1rem',
    backgroundColor: 'transparent',
    border: 'none',
    textAlign: 'left',
    cursor: 'pointer',
    color: '#dc3545',
    fontSize: '0.9rem',
    transition: 'background-color 0.2s ease',
  },
};

export default UserProfile;
